
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PropertyHighlight {
  title: string;
  description: string;
}

interface CreatePropertyHighlightsData {
  propertyId: string;
  highlights: PropertyHighlight[];
}

export const usePropertyHighlightMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createPropertyHighlights = useMutation({
    mutationFn: async ({ propertyId, highlights }: CreatePropertyHighlightsData) => {
      console.log('🏷️ Creating property highlights:', { propertyId, highlights });

      // Primero, eliminar highlights existentes para esta propiedad
      const { error: deleteError } = await supabase
        .from('property_highlights')
        .delete()
        .eq('property_id', propertyId);

      if (deleteError) {
        console.error('Error deleting existing highlights:', deleteError);
        throw deleteError;
      }

      // Insertar los nuevos highlights
      const highlightsToInsert = highlights.map(highlight => ({
        property_id: propertyId,
        title: highlight.title,
        description: highlight.description,
      }));

      const { data, error } = await supabase
        .from('property_highlights')
        .insert(highlightsToInsert)
        .select();

      if (error) {
        console.error('Error creating property highlights:', error);
        throw error;
      }

      console.log('✅ Property highlights created successfully:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-highlights'] });
      toast({
        title: "Características actualizadas",
        description: "Las características destacadas han sido guardadas exitosamente",
      });
    },
    onError: (error: any) => {
      console.error('❌ Error creating property highlights:', error);
      toast({
        title: "Error",
        description: `Error al guardar las características: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    createPropertyHighlights,
  };
};
